import { amountToNumber } from '../../../util/shared/tmc-lib';
import { toFiscalYear } from '../../../util/shared/tmc-global';
import { extractId } from '../../../util/shared/vue-global';

// unitCodeNumber -> the source altKey a detail line on that unit type must carry.
// Mirrors MiscDetails.vue's SOURCE_ALT_KEY_BY_UNIT_NUMBER so the receipt can be
// rejected before the dialog closes and the payload reaches the backend, not just
// flagged inline (see TKT-000622 follow-up: alt-key rule Save must stop, not just warn).
const SOURCE_ALT_KEY_BY_UNIT_NUMBER = {
  1: 'ABT',
  2: 'MV',
  3: 'MVTS',
  4: 'JT4MILL',
};

// same filters the apportionment build uses (getMiscABTRecords/getMiscMVRecords/
// getMiscMVTaxStampRecords/getMisc4MillRecords) to pick the tax units that fund each
// special unit type, so the allowed fund alt keys here match what the build resolves to.
// appSection comes back from GraphQL as the sharedBackPageEnum key ('B'/'F'/'G'), not the
// lowercase value ('b'/'f'/'g') the backend compares against internally - see taxUnitList.vue.
const TAX_UNIT_FILTER_BY_UNIT_NUMBER = {
  1: (record) => record.appSection === 'B',
  2: (record) => record.appSection === 'B',
  3: (record) => !!record.isMVTS,
  4: (record) => record.appSection === 'F' || record.appSection === 'G',
};

const findUnitCodeNumber = (item, unitCodes, useNewUnitCodeSetup) => {
  const match = useNewUnitCodeSetup
    ? (unitCodes || []).find((u) => u.unitCode === item.taxUnitCode)
    : (unitCodes || []).find((u) => extractId(item.unit) === extractId(u));
  return match ? match.unitCodeNumber : null;
};

const allowedFundAltKeys = (unitCodeNumber, taxUnits) => {
  const filterFn = TAX_UNIT_FILTER_BY_UNIT_NUMBER[unitCodeNumber];
  if (!filterFn) {
    return [];
  }
  const altKeys = new Set();
  (taxUnits || []).filter(filterFn).forEach((record) => {
    const fundDetail = unitCodeNumber === '4'
      ? (record.details || []).find((d) => d.fundDescription && d.fundDescription.startsWith('4'))
      : (record.details || []).find((d) => d.isGeneralFund === true);
    if (fundDetail && fundDetail.sharedFundAltKey) {
      altKeys.add(fundDetail.sharedFundAltKey);
    }
  });
  return [...altKeys];
};

export const sourceAltKeyRuleMessage = (item, {
  sources, unitCodes, useNewUnitCodeSetup, isVoid,
}) => {
  if (isVoid) {
    return null;
  }
  const unitCodeNumber = findUnitCodeNumber(item, unitCodes, useNewUnitCodeSetup);
  const requiredAltKey = SOURCE_ALT_KEY_BY_UNIT_NUMBER[unitCodeNumber];
  if (!requiredAltKey) {
    return null;
  }
  const source = (sources || []).find((s) => extractId(item.source) === extractId(s));
  if (!source || source.altKey !== requiredAltKey) {
    return `Unit ${requiredAltKey} requires a source with alt key ${requiredAltKey}`;
  }
  return null;
};

export const fundAltKeyRuleMessage = (item, {
  funds, unitCodes, taxUnits, useNewUnitCodeSetup, isVoid, allowFundExceptions,
}) => {
  if (isVoid || allowFundExceptions) {
    return null;
  }
  const unitCodeNumber = findUnitCodeNumber(item, unitCodes, useNewUnitCodeSetup);
  const requiredAltKey = SOURCE_ALT_KEY_BY_UNIT_NUMBER[unitCodeNumber];
  if (!requiredAltKey) {
    return null;
  }
  const allowedAltKeys = allowedFundAltKeys(unitCodeNumber, taxUnits);
  const fund = (funds || []).find((f) => extractId(item.fund) === extractId(f));
  if (!fund || !allowedAltKeys.includes(fund.altKey)) {
    const allowedList = allowedAltKeys.join(' or ') || '(none configured)';
    return `Unit ${requiredAltKey} money is apportioned to fund alt key ${allowedList}`;
  }
  return null;
};

// cmnFunds.isRequiredUnit only unlocked the unit field - it never made it mandatory, so a
// line on a required-unit fund could be saved with no unit and would apportion no money to
// the unit. The one exception mirrors apportionment's: with the apportionment setting
// allowUnitCodeOnAPageItems on, a required-unit fund with no back page (e.g. a Sheriff Service
// Fee fund) apportions straight to the fund in Section A and uses the unit only as an optional
// report breakdown, so the unit stays optional there. With the setting off, apportionment still
// rejects those lines ("Back Page missing"), so Save keeps requiring the unit.
// isApportionLikeTax lines hide the unit field entirely (the unit comes from the tax unit
// instead), so they're exempt, as are voids and $0 lines (same exceptions as the alt-key rules
// above). Mirrors validateMiscRequiredUnitRule on the backend.
export const fundHasBackPage = (fund) => !!(fund && fund.backPage && String(fund.backPage).trim());

export const requiredUnitRuleMessage = (item, {
  funds, useNewUnitCodeSetup, isVoid, isApportionLikeTax, allowUnitCodeOnAPageItems,
}) => {
  if (isVoid || isApportionLikeTax || amountToNumber(item.amount) === 0) {
    return null;
  }
  const fund = (funds || []).find((f) => extractId(item.fund) === extractId(f));
  if (!fund || !fund.isRequiredUnit || (allowUnitCodeOnAPageItems && !fundHasBackPage(fund))) {
    return null;
  }
  const hasUnit = useNewUnitCodeSetup ? !!item.taxUnitCode : !!item.unit;
  if (hasUnit) {
    return null;
  }
  return `Unit code is required for fund ${fund.fund}`;
};

// First unit/source/fund alt-key rule violation across a receipt's detail lines, or
// null. `context.isVoid` is the transaction-level void flag: voiding a receipt zeroes
// its amounts without changing fund/source/unit, so a receipt saved before this rule
// existed must still be voidable even with an unchanged bad combo.
export const firstAltKeyRuleFailure = (details, context) => {
  for (let i = 0; i < (details || []).length; i += 1) {
    const item = details[i];
    const message = sourceAltKeyRuleMessage(item, context)
      || fundAltKeyRuleMessage(item, context)
      || requiredUnitRuleMessage(item, context);
    if (message) {
      return message;
    }
  }
  return null;
};

export const buildPaymentItem = (element) => {
  if (element.paymentType) {
    const amount = amountToNumber(element.amount);
    const updatedElement = {
      paymentType: element.paymentType,
      isReconcilled: element.isReconcilled,
      amount,
    };
    if (element.paymentType === 'CASH') {
      updatedElement.cashReceived = amountToNumber(element.cashReceived);
      updatedElement.cashChange = amountToNumber(element.cashChange);
    }
    if (element.paymentType === 'CREDIT') {
      updatedElement.creditType = element.creditType;
    }
    if (element.paymentType === 'CHECK') {
      updatedElement.checkNumber = element.checkNumber;
      updatedElement.checkPaidBy = element.checkPaidBy;
    }
    if (element.paymentType === 'EFT') {
      updatedElement.eftType = element.eftType;
    }
    return { ...updatedElement };
  }
  return { ...element };
};

export const determineCssProps = (transaction, miscSettings) => {
  const cssProp = ['orange', 'accent-1'];
  const unpaidCssProp = ['red', 'accent-2'];
  const mismatchedDetailCssProp = ['blue', 'accent-1'];

  const fullAmount = amountToNumber(transaction.amount);
  const amountPaid = (transaction.paymentDetails
    || []).reduce((sum, detail) => sum + (detail.amount ? amountToNumber(detail.amount) : 0.00),
    0.00);
  const formattedAmountPaid = amountToNumber(amountPaid);
  const isPaidInFull = fullAmount === formattedAmountPaid;

  const detailAmountSum = (transaction.details
    || []).reduce((sum, detail) => sum + (detail.amount ? amountToNumber(detail.amount) : 0.00),
    0.00);
  const formattedDetailAmountSum = amountToNumber(detailAmountSum);
  const isDetailAmtMatched = fullAmount === formattedDetailAmountSum;

  if (transaction.isVoid === true) {
    return cssProp;
  } if (transaction.isCorrection === true) {
    return [];
  } if (transaction.transactionNumberObject) {
    return [];
  }
  if (transaction.amount === 0 && detailAmountSum === 0
     && (transaction.paymentDetails || []).length === 0) {
    return [];
  }
  if ((transaction.paymentDetails || []).length === 0 || !isPaidInFull) {
    if (miscSettings && miscSettings.ignoreOutOfBalanceReceipts) {
      return [];
    }
    return unpaidCssProp;
  } if ((transaction.details || []).length === 0 || !isDetailAmtMatched) {
    return mismatchedDetailCssProp;
  }
  return [];
};

export const checkTransactionDetails = (item) => {
  const requiredDetailsBooleanArray = [];
  for (let i = 0; i < item.details.length; i += 1) {
    const detail = item.details[i];
    const hasRequiredDetails = !!(detail.fund || detail.taxDistrict);
    requiredDetailsBooleanArray.push(hasRequiredDetails);
  }
  const allDetailsHaveReqdFields = requiredDetailsBooleanArray.every((value) => value === true);

  const fullAmount = amountToNumber(item.amount);
  const amountPaid = (item.paymentDetails
      || []).reduce((sum, detail) => sum + (detail.amount ? amountToNumber(detail.amount) : 0.00),
    0.00);
  const formattedAmountPaid = amountToNumber(amountPaid);
  const isPaidInFull = fullAmount === formattedAmountPaid;

  const detailAmountSum = (item.details
      || []).reduce((sum, detail) => sum + (detail.amount ? amountToNumber(detail.amount) : 0.00),
    0.00);
  const formattedDetailAmountSum = amountToNumber(detailAmountSum);
  const isDetailAmtMatched = fullAmount === formattedDetailAmountSum;

  return { allDetailsHaveReqdFields, isPaidInFull, isDetailAmtMatched };
};

export default {};

// True when `fiscalYear` has already ended as of `todaysDate` ("YYYY-MM-DD"). A new receipt may
// not be created in such a year unless the user is a Misc admin (backend enforces the same).
export const isPastFiscalYear = (fiscalYear, todaysDate) => {
  const year = Number(fiscalYear);
  const currentYear = toFiscalYear(new Date(todaysDate));
  return !!year && !!currentYear && year < currentYear;
};
